import Papa from 'papaparse';
import { Socio } from '../types';

export type SocioField = 'nombre' | 'apellido' | 'telefono' | 'email' | 'notas' | 'ignore';

export interface RawRow {
    [header: string]: string;
}

export interface ColumnMap {
    [csvHeader: string]: SocioField;
}

export interface ParsedSocio {
    nombre: string;
    apellido: string;
    telefono: string;
    email: string | null;
    notas: string;
}

export interface ValidationResult {
    toImport: ParsedSocio[];
    duplicates: { row: ParsedSocio; matchedWith: Pick<Socio, 'id' | 'nombre' | 'apellido' | 'telefono' | 'email'> }[];
    invalid: { row: RawRow; reason: string }[];
}

const HEADER_ALIASES: Record<string, SocioField> = {
    'first name': 'nombre',
    'given name': 'nombre',
    'nombre': 'nombre',
    'last name': 'apellido',
    'family name': 'apellido',
    'apellido': 'apellido',
    'phone 1 - value': 'telefono',
    'phone': 'telefono',
    'phone number': 'telefono',
    'mobile': 'telefono',
    'telefono': 'telefono',
    'teléfono': 'telefono',
    'e-mail 1 - value': 'email',
    'email 1 - value': 'email',
    'email': 'email',
    'e-mail': 'email',
    'notes': 'notas',
    'notas': 'notas',
};

export function autoMapColumns(headers: string[]): ColumnMap {
    const map: ColumnMap = {};
    for (const h of headers) {
        const key = h.trim().toLowerCase();
        map[h] = HEADER_ALIASES[key] || 'ignore';
    }
    return map;
}

export function parseGoogleCsv(file: File): Promise<{ headers: string[]; rows: RawRow[] }> {
    return new Promise((resolve, reject) => {
        Papa.parse<RawRow>(file, {
            header: true,
            skipEmptyLines: true,
            transformHeader: h => h.trim(),
            complete: (results) => {
                const headers = results.meta.fields || [];
                resolve({ headers, rows: results.data as RawRow[] });
            },
            error: (err) => reject(err),
        });
    });
}

export function normalizePhone(raw: string | undefined | null): string {
    if (!raw) return '';
    const first = String(raw).split(/[:;,]/)[0];
    let digits = first.replace(/\D/g, '');
    if (!digits) return '';
    if (digits.startsWith('54')) digits = digits.slice(2);
    if (digits.startsWith('9') && digits.length > 10) digits = digits.slice(1);
    if (digits.startsWith('0')) digits = digits.replace(/^0+/, '');
    return digits;
}

function normalizeEmail(raw: string | undefined | null): string | null {
    if (!raw) return null;
    const first = String(raw).split(/[;,]/)[0].trim().toLowerCase();
    if (!first || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(first)) return null;
    return first;
}

function applyMapping(row: RawRow, mapping: ColumnMap): { parsed: ParsedSocio | null; reason?: string } {
    const fields: Record<SocioField, string> = { nombre: '', apellido: '', telefono: '', email: '', notas: '', ignore: '' };
    for (const [header, target] of Object.entries(mapping)) {
        if (target === 'ignore') continue;
        const val = (row[header] || '').trim();
        if (val && !fields[target]) fields[target] = val;
    }
    const telefono = normalizePhone(fields.telefono);
    const email = normalizeEmail(fields.email);
    const nombre = fields.nombre.trim();
    const apellido = fields.apellido.trim();

    if (!nombre && !apellido) {
        return { parsed: null, reason: 'Sin nombre ni apellido' };
    }
    if (!telefono && !email) {
        return { parsed: null, reason: 'Sin teléfono ni email' };
    }
    return {
        parsed: {
            nombre: nombre || apellido,
            apellido: nombre ? apellido : '',
            telefono,
            email,
            notas: fields.notas,
        },
    };
}

export function validateAndDedupe(
    rows: RawRow[],
    mapping: ColumnMap,
    existingSocios: Pick<Socio, 'id' | 'nombre' | 'apellido' | 'telefono' | 'email'>[],
): ValidationResult {
    const result: ValidationResult = { toImport: [], duplicates: [], invalid: [] };

    const phoneIdx = new Map<string, typeof existingSocios[number]>();
    const emailIdx = new Map<string, typeof existingSocios[number]>();
    for (const s of existingSocios) {
        const p = normalizePhone(s.telefono);
        if (p) phoneIdx.set(p, s);
        if (s.email) emailIdx.set(s.email.toLowerCase(), s);
    }

    const seenPhones = new Set<string>();
    const seenEmails = new Set<string>();

    for (const row of rows) {
        const { parsed, reason } = applyMapping(row, mapping);
        if (!parsed) {
            result.invalid.push({ row, reason: reason || 'Datos insuficientes' });
            continue;
        }
        const dupExisting =
            (parsed.telefono && phoneIdx.get(parsed.telefono)) ||
            (parsed.email && emailIdx.get(parsed.email));
        if (dupExisting) {
            result.duplicates.push({ row: parsed, matchedWith: dupExisting });
            continue;
        }
        // Intra-CSV dedupe
        if (parsed.telefono && seenPhones.has(parsed.telefono)) {
            result.duplicates.push({
                row: parsed,
                matchedWith: { id: -1, nombre: '(duplicado en el CSV)', apellido: '', telefono: parsed.telefono, email: null },
            });
            continue;
        }
        if (parsed.email && seenEmails.has(parsed.email)) {
            result.duplicates.push({
                row: parsed,
                matchedWith: { id: -1, nombre: '(duplicado en el CSV)', apellido: '', telefono: '', email: parsed.email },
            });
            continue;
        }
        if (parsed.telefono) seenPhones.add(parsed.telefono);
        if (parsed.email) seenEmails.add(parsed.email);
        result.toImport.push(parsed);
    }
    return result;
}
